import React, { useEffect, useMemo, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import debounce from 'lodash.debounce'

import { IconPlus } from 'components/Icons/IconPlus'
import { IconMinusPrice } from 'components/Icons/IconMinusPrice'

interface PercentageInputProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
}

const Container = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${p => p.theme.colors.divider};
  border-radius: 8px;
  background: ${p => p.theme.colors.background.secondary};
  width: fit-content;
  overflow: hidden;
  padding: 10px;
`

const Button = styled.button`
  background: transparent;
  display: flex;
  border: none;
  padding: 0px;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  color: ${p => p.theme.colors.text.primary};
`

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  background: transparent;
  position: relative;
  min-width: 60px;
  padding: 0 8px;
`

const StyledInput = styled.input`
  width: 100%;
  text-align: center;
  border: none;
  background: transparent;
  font-size: 16px;
  color: ${p => p.theme.colors.text.primary};
  font-weight: ${p => p.theme.typography.weight.Bold};
  font-size: ${p => p.theme.typography.size.Body}px;

  &:focus {
    outline: none;
  }
`

const DELAY = 500

export const PercentageInput = ({ value, onChange, min = 0, max = 100, step = 0.05 }: PercentageInputProps) => {
  const theme = useTheme()

  const [inputValue, setInputValue] = useState(`${value}%`)
  const [localValue, setLocalValue] = useState<number>(value)

  useEffect(() => {
    setLocalValue(value)
    setInputValue(`${value}%`)
  }, [value])

  const clamp = (val: number) => Math.min(Math.max(val, min), max)

  const debouncedChange = useMemo(() => debounce(onChange, DELAY), [onChange])

  const updateValue = (newVal: number) => {
    const clamped = clamp(newVal)
    const roundedValue = Math.round(clamped * 100) / 100
    setLocalValue(roundedValue)
    setInputValue(`${roundedValue}%`)
    debouncedChange(roundedValue)
  }

  const handleDecrease = () => {
    updateValue(localValue - step)
  }

  const handleIncrease = () => {
    updateValue(localValue + step)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace('%', '').trim()

    if (/^\d*\.?\d{0,10}$/.test(raw)) {
      setInputValue(raw + '%')
    }
  }

  const handleOnBlur = () => {
    const raw = inputValue.replace('%', '').trim()
    const parsed = parseFloat(raw)

    if (!isNaN(parsed)) {
      const clamped = clamp(parsed)
      setLocalValue(clamped)
      setInputValue(`${clamped}%`)
      onChange(clamped)
    } else {
      // fallback to last valid value
      setInputValue(`${localValue}%`)
    }
  }

  useEffect(() => {
    return () => debouncedChange.cancel()
  }, [debouncedChange])

  return (
    <Container>
      <Button onClick={handleDecrease}>
        <IconMinusPrice stroke={theme.colors.text.primary} />
      </Button>
      <InputWrapper>
        <StyledInput value={inputValue} onChange={handleInputChange} onBlur={handleOnBlur} />
      </InputWrapper>
      <Button onClick={handleIncrease}>
        <IconPlus stroke={theme.colors.text.primary} />
      </Button>
    </Container>
  )
}
